import { freezeAppConfigSingleton, useAppConfigAndServiceProviderOverride } from "@expert/app-config";
import { useToast } from "@expert/common-ui";
import { partnerEnabledForExpertUniversity } from "@expert/expert-university/src/utils/partner";
import { GaiaWebsocketProvider } from "@expert/gaia";
import { enableWorkerLogging, getLogger } from "@expert/logging";
import { getAllowedPartnersOverride, getServiceProviders } from "@expert/service-providers";
import type { Partner } from "@expert/shared-types";
import { ExpertWorkspaceWebSocketProvider } from "@expert/websocket";
import { Button, Flex, Group } from "@mantine/core";
import { useNetwork } from "@mantine/hooks";
import type React from "react";
import { useEffect, useMemo, useRef } from "react";
import { withErrorBoundary } from "react-error-boundary";
import { useAnalytics } from "@/analytics";
import { useSetupSsoToken } from "@/auth";
import { GlobalError } from "@/common-ui";
import { WorkspaceLoader } from "@/common-ui/WorkspaceLoader";
import { Workspace } from "@/components";
import EvlConnectionNotifications from "@/components/evl/EvlConnectionNotifications";
import { env } from "@/config";
import ExpertUniversitySdk from "@/expert-university/ExpertUniversitySdk";
import { WorkspaceFeatureFlags } from "@/features";
import { useAgentAttributesStore } from "@/sales-tools/stores/agent-attributes.store";
import { AgentSdkProvider } from "@/sdk/agent/AgentSdkProvider";
import { getAgentLocation } from "@/sdk/agent/utils/getAgentLocation";
import { createFallbackRender, errorEventBus, getSharedApiClient, useFeatureFlag, userCache } from "@/shared-utils";
import { USER_FRIENDLY_NAME_QS_KEY } from "@/shared-utils/constants";

const logger = getLogger({ module: "Home" });

const WorkspaceHandled = withErrorBoundary(Workspace, {
    fallbackRender: createFallbackRender(logger),
});

export default function Home(): React.JSX.Element {
    const { data: isEvlConnectionNotificationsEnabled = false } = useFeatureFlag(
        "EWP-isEvlConnectionNotificationsEnabled",
    );
    const environment = import.meta.env.VITE_ENV_TYPE as string;
    const employeeId = userCache.employeeId ?? undefined;
    const userFriendlyName =
        environment === "development"
            ? (new URLSearchParams(window.location.search).get(USER_FRIENDLY_NAME_QS_KEY) ?? undefined)
            : undefined;
    const allowedPartnersOverride = getAllowedPartnersOverride();
    const toast = useToast();
    const { dispatcher } = useAnalytics();
    const auth = useSetupSsoToken(userFriendlyName);
    const { data: isWebsocketEnabled = true, isLoading: isLoadingWebsocketEnabled } = useFeatureFlag(
        "EWP-isWebsocketEnabled",
        {
            org: auth.user?.profile.org,
        },
    );

    // TODO: Remove Twilio from here
    const {
        data: [appConfig, serviceProvidersOverride] = [],
        isLoading: appConfigLoading,
        error: appConfigError,
    } = useAppConfigAndServiceProviderOverride(getSharedApiClient(), employeeId);
    const partner = allowedPartnersOverride?.length
        ? allowedPartnersOverride[0]
        : (appConfig?.allowedPartners.split(",")[0] as Partner | undefined);
    const expertUniversitySdk = useMemo(() => {
        return partner && partnerEnabledForExpertUniversity(partner, env.channel)
            ? new ExpertUniversitySdk()
            : undefined;
    }, [partner]);

    const serviceProviders = useMemo(() => {
        if (env.channel === "chat") {
            // Multi-channel experts in chat mode should avoid using the voice specific feature flag
            return getServiceProviders(partner, env.channel);
        }
        return serviceProvidersOverride ?? getServiceProviders(partner, env.channel);
    }, [partner, serviceProvidersOverride]);
    const partnerConfig =
        appConfig && partner && appConfig.partnersConfig[partner in appConfig.partnersConfig ? partner : "others"];

    useEffect(() => {
        const errorUnsub = errorEventBus.on("error-occurred", (error) => {
            logger.error({ err: error }, "Error running loading action:");

            if (error instanceof Error) {
                toast.error(`An unexpected error occurred: ${error.message}`);
            } else {
                toast.error("An unexpected error occurred");
            }
        });

        return () => {
            errorUnsub();
        };
    }, [toast]);

    useFeatureFlag("EWP-isWorkerLoggingEnabled", undefined, {
        onResponse: (isWorkerLoggingEnabled = false) => {
            if (isWorkerLoggingEnabled) enableWorkerLogging();
        },
    });

    const handlePageReload = () => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
    };

    useEffect(() => {
        const getAgentSite = async () => {
            const { locationId: site } = await getAgentLocation();
            useAgentAttributesStore.getState().setAgentSite(site);
        };
        getAgentSite();
    }, []);

    useEffect(() => {
        if (!auth.user && auth.isLoading) {
            void dispatcher.dispatchBusinessEvent("Authenticating");
        }
    }, [dispatcher, auth.user, auth.isLoading]);

    // normally, `useState()` is desirable but in this case we need to change the value and read it before React rerenders
    const isAuthRedirectNeededRef = useRef(false);
    function handleOnAuthRedirect() {
        logger.warn("Redirecting for agent sdk re-authentication");
        isAuthRedirectNeededRef.current = true;
    }

    const networkStatus = useNetwork();
    useEffect(() => {
        logger.info(networkStatus, "Network status");
    }, [networkStatus]);

    if (!auth.error && auth.isLoading) {
        return <WorkspaceLoader msg="Acquiring access token..." />;
    }

    if (appConfigLoading || isLoadingWebsocketEnabled) {
        return <WorkspaceLoader msg="Checking if experimental features should be enabled..." />;
    }

    if (auth.error || !auth.user) {
        logger.error({ err: auth.error }, "Error authenticating");
        return (
            <GlobalError msg="You can also contact Service Desk for help or if it’s your first time using Expert Workspace.">
                <Group>
                    <Button mt="sm" onClick={handlePageReload} variant="outline" c="white" color="asurion.5">
                        Refresh
                    </Button>
                </Group>
            </GlobalError>
        );
    }

    if (!appConfigError && (appConfigLoading || !appConfig)) {
        void dispatcher.dispatchAction("LoadingConfiguration", "LoadingConfiguration");
        return <WorkspaceLoader msg="Loading configuration..." />;
    }

    if ((!appConfigLoading && !appConfig) || appConfigError) {
        logger.error({ err: appConfigError }, "Error loading configuration");
        return (
            <GlobalError title="Error loading Workspace configuration">
                <Button mt="sm" onClick={handlePageReload}>
                    Refresh
                </Button>
            </GlobalError>
        );
    }

    freezeAppConfigSingleton({ appConfig: appConfig!, org: userCache.org, channel: env.channel });

    const { ssoAccessToken, identity } = userCache;
    if (!ssoAccessToken) return <h1>DEBUG: NO token</h1>;
    if (!identity) return <h1>DEBUG: NO identity</h1>;
    if (!employeeId) {
        logger.error({ userCache }, "No employee ID for user.");
        return <h1>DEBUG: NO employee ID</h1>;
    }

    return (
        <Flex flex={1} direction="column" h="100%" w="100%">
            {/* TODO: Move relevant providers up once we have several pages */}
            <ExpertWorkspaceWebSocketProvider
                identity={employeeId}
                token={ssoAccessToken}
                webSocketUrl={env.expertWorkspaceWebSocketUrl}
                useSocketIO={env.useSocketIO}
                isWebsocketEnabled={isWebsocketEnabled}
            >
                <AgentSdkProvider
                    employeeNum={employeeId}
                    userFriendlyName={userFriendlyName}
                    serviceProviders={serviceProviders}
                    partnerConfig={partnerConfig}
                    additionalSdks={expertUniversitySdk ? [expertUniversitySdk] : undefined}
                    onAuthRedirect={handleOnAuthRedirect}
                >
                    {env.channel !== "chat" && isEvlConnectionNotificationsEnabled && <EvlConnectionNotifications />}
                    {/* TODO: Get client from the worker attributes maybe? */}
                    <GaiaWebsocketProvider
                        identity={identity}
                        application="expert-workspace"
                        token={ssoAccessToken}
                        url={env.gaiaWebsocketUrl}
                        logger={logger}
                    >
                        <WorkspaceFeatureFlags employeeId={employeeId} logger={logger}>
                            <WorkspaceHandled isAuthRedirectNeededRef={isAuthRedirectNeededRef} />
                        </WorkspaceFeatureFlags>
                    </GaiaWebsocketProvider>
                </AgentSdkProvider>
            </ExpertWorkspaceWebSocketProvider>
        </Flex>
    );
}
